import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Paper, Typography, TextField, Button,
    Alert, InputAdornment, CircularProgress,
} from '@mui/material';
import {
    Lock as LockIcon,
    CalendarMonth as LogoIcon,
    CheckCircle as DoneIcon,
} from '@mui/icons-material';
import { auth } from '../services/api';

const ResetPassword = () => {
    const navigate       = useNavigate();
    const [searchParams] = useSearchParams();
    const token          = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirm,  setConfirm]  = useState('');
    const [done,     setDone]     = useState(false);
    const [error,    setError]    = useState('');
    const [loading,  setLoading]  = useState(false);

    if (!token) return (
        <Box sx={{ minHeight:'100vh', bgcolor:'#F8FAFC', display:'flex', alignItems:'center', justifyContent:'center', px:2 }}>
            <Box sx={{ maxWidth:420, width:'100%' }}>
                <Alert severity="error" sx={{ borderRadius:3 }}>
                    Invalid or missing reset token. Please request a new link.
                </Alert>
            </Box>
        </Box>
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
        if (password !== confirm) { setError('Passwords do not match.'); return; }
        setLoading(true);
        try {
            await auth.resetPassword(token, password);
            setDone(true);
        } catch (err) {
            setError(err.response?.data?.message || 'Reset link is invalid or has expired. Please request a new one.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight:'100vh', bgcolor:'#F8FAFC', display:'flex', alignItems:'center', justifyContent:'center', px:2, py:6 }}>
            <Box sx={{ width:'100%', maxWidth:420 }}>

                <Paper elevation={0} variant="outlined" sx={{ borderRadius:4, overflow:'hidden', borderColor:'rgba(0,0,0,0.1)' }}>

                    {/* Brand header */}
                    <Box sx={{ background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', px:4, pt:5, pb:4, position:'relative', overflow:'clip' }}>
                        <Box sx={{ position:'absolute', top:-30, right:-20, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.07)', pointerEvents:'none' }} />
                        <Box sx={{ position:'absolute', bottom:-40, left:-10, width:120, height:120, borderRadius:'50%', background:'rgba(255,255,255,0.05)', pointerEvents:'none' }} />
                        <Box sx={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', gap:2 }}>
                            <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                                <Box sx={{ width:40, height:40, borderRadius:2, bgcolor:'rgba(255,255,255,0.18)', border:'1.5px solid rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                    <LogoIcon sx={{ fontSize:22, color:'white' }} />
                                </Box>
                                <Typography variant="h6" fontWeight={800} sx={{ color:'white', letterSpacing:-0.3 }}>Quuip</Typography>
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={800} sx={{ color:'white', lineHeight:1.2 }}>Set a new password</Typography>
                                <Typography variant="body2" sx={{ color:'rgba(255,255,255,0.75)', mt:0.5 }}>Choose something strong and memorable</Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Body */}
                    <Box sx={{ px:4, py:4, bgcolor:'white', display:'flex', flexDirection:'column', gap:3 }}>
                        {done ? (
                            <Box sx={{ textAlign:'center', py:2 }}>
                                <Box sx={{ width:64, height:64, borderRadius:'50%', background:'linear-gradient(135deg,#DCFCE7,#BBF7D0)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:2.5 }}>
                                    <DoneIcon sx={{ fontSize:32, color:'#16A34A' }} />
                                </Box>
                                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color:'#0F172A' }}>Password updated!</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight:1.7, mb:3 }}>
                                    You're all set. Sign in with your new password.
                                </Typography>
                                <Button variant="contained" onClick={() => navigate('/login')}
                                    sx={{ fontWeight:700, borderRadius:2.5, px:4, background:'linear-gradient(135deg,#7C3AED,#4F46E5)', boxShadow:'0 4px 14px rgba(124,58,237,0.3)' }}>
                                    Sign In
                                </Button>
                            </Box>
                        ) : (
                            <>
                                {error && <Alert severity="error" sx={{ borderRadius:2 }}>{error}</Alert>}
                                <Box component="form" onSubmit={handleSubmit} sx={{ display:'flex', flexDirection:'column', gap:2.5 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75, fontSize:'0.65rem' }}>New password</Typography>
                                        <TextField fullWidth required type="password" size="small" autoFocus
                                            placeholder="At least 8 characters"
                                            value={password} onChange={e => setPassword(e.target.value)}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon sx={{ fontSize:17, color:'text.disabled' }} /></InputAdornment> }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75, fontSize:'0.65rem' }}>Confirm new password</Typography>
                                        <TextField fullWidth required type="password" size="small"
                                            placeholder="Repeat your new password"
                                            value={confirm} onChange={e => setConfirm(e.target.value)}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon sx={{ fontSize:17, color:'text.disabled' }} /></InputAdornment> }} />
                                    </Box>
                                    <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}
                                        sx={{ fontWeight:700, borderRadius:2.5, py:1.5, background:'linear-gradient(135deg,#7C3AED,#4F46E5)', boxShadow:'0 4px 14px rgba(124,58,237,0.3)' }}>
                                        {loading ? <CircularProgress size={20} sx={{ color:'white' }} /> : 'Update Password'}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default ResetPassword;
